// CreateWaiverForm.jsx
import React, { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { CREATE_WAIVER } from "./Waiver";
import { GlobalStyles } from "../../constants/styles";
import useTranslation from "../../i18n/useTranslation";

export default function CreateWaiverForm({ onCreated, onClose }) {
    const scopeId = "kbola-create-waiver-form";
    const { t, supportedLanguages } = useTranslation();

    const [title, setTitle] = useState("");
    const [waiverLanguage, setWaiverLanguage] = useState("en");
    const [waiverType, setWaiverType] = useState("TEXT");
    const [content, setContent] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const [createWaiver] = useMutation(CREATE_WAIVER);

    const ui = useMemo(() => {
        return {
            fieldLabel: {
                fontWeight: 900,
                fontSize: 12.5,
                color: "rgba(20,21,26,0.72)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                fontWeight: 800,
                color: "#14151a",
            },
            textarea: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                fontWeight: 800,
                color: "#14151a",
                resize: "vertical",
                minHeight: 120,
            },
            helper: {
                marginTop: 6,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },

            footer: {
                paddingTop: 14,
                marginTop: 14,
                borderTop: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            btnDisabled: { opacity: 0.7, cursor: "not-allowed" },

            fileWrap: {
                borderRadius: 14,
                border: "1px dashed rgba(0,0,0,0.18)",
                background: "rgba(0,0,0,0.015)",
                padding: 12,
            },
            fileRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
            },
            fileName: {
                fontWeight: 900,
                color: "#14151a",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            fileBtn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            hiddenFile: { position: "absolute", left: -9999, opacity: 0 },
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (uploading) return;

        setUploading(true);
        try {
            const variables = {
                input: {
                    title,
                    language: waiverLanguage,
                    waiverType,
                    content: waiverType === "TEXT" ? content : null,
                    file: waiverType === "DOCUMENT" ? file : null,
                },
            };

            const { data } = await createWaiver({ variables });

            if (data?.createWaiver?.waiver) {
                onCreated(data.createWaiver.waiver);
            }
            onClose();
        } catch (err) {
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    const isDoc = waiverType === "DOCUMENT";
    const canSubmit = title.trim().length > 0 && (isDoc ? !!file : true) && !uploading;

    const fileInputId = "kbola-waiver-file";
    const languageLabels = { en: "English", es: "Español", fr: "Français" };

    return (
        <form id={scopeId} onSubmit={handleSubmit}>
            <div className="form-group">
                <label style={ui.fieldLabel}>{t("createWaiverForm.title")}</label>
                <input
                    className="form-control"
                    style={ui.input}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    disabled={uploading}
                    placeholder={t("createWaiverForm.titlePlaceholder")}
                />
            </div>

            <div className="form-group">
                <label style={ui.fieldLabel}>{t("createWaiverForm.language")}</label>
                <select
                    className="form-control"
                    style={ui.input}
                    value={waiverLanguage}
                    onChange={(e) => setWaiverLanguage(e.target.value)}
                    disabled={uploading}
                >
                    {supportedLanguages.map((langCode) => (
                        <option key={langCode} value={langCode}>
                            {languageLabels[langCode] || langCode.toUpperCase()}
                        </option>
                    ))}
                </select>
            </div>

            <div className="form-group">
                <label style={ui.fieldLabel}>{t("createWaiverForm.waiverType")}</label>
                <select
                    className="form-control"
                    style={ui.input}
                    value={waiverType}
                    onChange={(e) => {
                        const next = e.target.value;
                        setWaiverType(next);
                        if (next === "TEXT") setFile(null);
                        if (next === "DOCUMENT") setContent("");
                    }}
                    disabled={uploading}
                >
                    <option value="TEXT">{t("createWaiverForm.text")}</option>
                    <option value="DOCUMENT">{t("createWaiverForm.documentPdf")}</option>
                </select>
            </div>

            {waiverType === "TEXT" ? (
                <div className="form-group">
                    <label style={ui.fieldLabel}>{t("createWaiverForm.content")}</label>
                    <textarea
                        className="form-control"
                        style={ui.textarea}
                        rows={6}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        disabled={uploading}
                        placeholder={t("createWaiverForm.contentPlaceholder")}
                    />
                    <div style={ui.helper}>{t("createWaiverForm.contentHelper")}</div>
                </div>
            ) : (
                <div className="form-group">
                    <label style={ui.fieldLabel}>{t("createWaiverForm.pdfDocument")}</label>

                    <div style={ui.fileWrap}>
                        <div style={ui.fileRow}>
                            <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                                <div style={ui.fileName} title={file?.name || ""}>
                                    {file ? file.name : t("createWaiverForm.noFileSelected")}
                                </div>
                                <div style={ui.helper}>{t("createWaiverForm.pdfHelper")}</div>
                            </div>

                            <div style={{ flex: "0 0 auto" }}>
                                <label htmlFor={fileInputId} style={ui.fileBtn}>
                                    <i className="fas fa-upload" />
                                    {t("createWaiverForm.choosePdf")}
                                </label>
                                <input
                                    id={fileInputId}
                                    type="file"
                                    style={ui.hiddenFile}
                                    accept="application/pdf"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    required={isDoc}
                                    disabled={uploading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div style={ui.footer}>
                <button
                    type="button"
                    style={{ ...ui.btn, ...(uploading ? ui.btnDisabled : {}) }}
                    onClick={onClose}
                    disabled={uploading}
                    title={t("createWaiverForm.close")}
                >
                    <i className="fas fa-times" />
                    {t("createWaiverForm.close")}
                </button>

                <button
                    type="submit"
                    style={{
                        ...ui.btn,
                        ...ui.btnPrimary,
                        ...(!canSubmit ? ui.btnDisabled : {}),
                    }}
                    disabled={!canSubmit}
                    title={t("createWaiverForm.createWaiver")}
                >
                    {uploading ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            {t("createWaiverForm.uploading")}
                        </>
                    ) : (
                        <>
                            <i className="fas fa-plus" />
                            {t("createWaiverForm.createWaiver")}
                        </>
                    )}
                </button>
            </div>

            <style>{`
        #${scopeId} .form-control:focus,
        #${scopeId} select:focus,
        #${scopeId} textarea:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          border-color: rgba(0,0,0,0.18);
        }

        #${scopeId} i.fas,
        #${scopeId} i.far,
        #${scopeId} i.fal,
        #${scopeId} i.fa,
        #${scopeId} i[class^="fa-"],
        #${scopeId} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        #${scopeId} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }
      `}</style>
        </form>
    );
}
